import Form, { Field, Props as FormProps } from '@/components/Form';

import { clinicCrud, invitationCrud, patientCrud, userCrud } from '@/services/firebase';
import { createClinic, createInvitation, createPatientInvitation } from '@/services/api';
import { useNavigate, useParams } from 'react-router-dom';
import { Dict } from '@ajorquera/libs/utils';
import { capitalizeStr, mergeValuesIntoFields } from '@/utils/helpers';

import { arrayUnion } from 'firebase/firestore';
import { FC, useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';

import PageContainer from '@/components/PageContainer';
import useAsync from '@/hooks/useAsync';
import { useAuth } from '@/components/auth';
import { User } from '@/types/database';

export interface Props {
  entity: User['role'] | 'patient';
  readonly?: boolean;
  extraDetails?: Dict<unknown>;
}

/**
 * EntityDetails
 */
const EntityDetails: FC<Props> = ({ entity, readonly, extraDetails = {} }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { id, clinicId } = useParams<{ id: string; clinicId: string }>();
  const { user, getToken } = useAuth();
  const navigate = useNavigate();

  const isNew = id === 'new';

  const entityCapitalized = capitalizeStr(entity);

  const entityPrefix = isNew ? 'new' : readonly ? 'view ' : 'edit';
  const title = `${entityPrefix}${entityCapitalized}Title`;
  const description = `${entityPrefix}${entityCapitalized}Description`;

  const pageProps = ENTITY_MAP[entity];
  const fields = pageProps.fields;

  const { loading: loadingCreate, execute: create, error: errorCreate } = useAsync(pageProps.create);
  const { loading: loadingUpdate, execute: update, error: errorUpdate } = useAsync(pageProps.update);
  const {
    loading: loadingRead,
    data: entityObj,
    error: errorRead,
  } = useAsync(() => pageProps.read(id), { inmediate: !!id });

  useEffect(() => {
    if (!entityObj && !loadingRead && !isNew && !errorRead) {
      enqueueSnackbar(`${entityCapitalized} not found`, { variant: 'error' });
      navigate('..');
    }
  }, [entityObj, loadingRead, errorRead, isNew]);

  const onSubmit = async (data: Record<string, unknown>) => {
    if (clinicId) {
      extraDetails.facilityName = await clinicCrud.read(clinicId as string).then((c) => c.name);
    }

    if (entity === 'clinic-admin') {
      extraDetails.managedClinicId = clinicId;
    } else if (entity === 'doctor') {
      extraDetails.associatedClinicIds = arrayUnion(clinicId);
    }
    const token = await getToken();
    
    if (isNew) {
      await create(
        {
          ...data,
          ...extraDetails,
        },
        user?.id as string,
        clinicId as string,
        token
      );

      enqueueSnackbar('Invitation sent', {
        variant: 'success',
        anchorOrigin: { horizontal: 'center', vertical: 'top' },
      });
    } else {
      await update(id as string, {
        ...data,
      });
    }

    navigate(-1);
  };

  const loading = loadingCreate || loadingUpdate;
  const error = errorCreate || errorUpdate;

  const innerFields = useMemo(
    () => mergeValuesIntoFields({ ...entityObj, ...extraDetails } as unknown as Dict<string>, fields),
    [fields, entityObj]
  );
  return (
    <PageContainer goback loading={loadingRead} error={errorRead?.message} title={title} description={description}>
      <Form loading={loading} error={error?.message} disabled={!isNew} fields={innerFields} onSubmit={onSubmit} />
    </PageContainer>
  );
};

export default EntityDetails;
type Action = (...args: any) => Promise<any>;

interface PageProps {
  fields: FormProps['fields'];
  create: Action;
  update: Action;
  read: Action;
}

type entities = 'clinic' | 'invitation-doctor' | 'invitation-patient' | 'invitation-clinic-admin';

const INVITATION_COMMON_FIELDS: Field[] = [
  {
    name: 'name',
    type: 'text-field',
    label: 'Name',
    options: {
      required: true,
    },
  },
  {
    name: 'email',
    type: 'text-field',
    label: 'Email',
    options: {
      required: true,
      type: 'email',
    },
  },
  {
    name: 'phone',
    type: 'text-field',
    label: 'Phone',
    options: {
      required: true,
    },
  },
];

const ENTITY_MAP: Record<Props['entity'], PageProps> = {
  'clinic-admin': {
    fields: [
      ...INVITATION_COMMON_FIELDS,

      {
        name: 'role',
        type: 'hidden',
        value: 'clinic-admin',
      },
    ],
    create: createInvitation,
    update: invitationCrud.update.bind(invitationCrud),
    read: userCrud.read.bind(userCrud),
  },
  doctor: {
    fields: [
      ...INVITATION_COMMON_FIELDS,
      {
        name: 'role',
        type: 'hidden',
        value: 'doctor',
      },
    ],
    create: createInvitation,
    update: invitationCrud.update.bind(invitationCrud),
    read: userCrud.read.bind(userCrud),
  },
  patient: {
    fields: [
      {
        name: 'name',
        type: 'text-field',
        label: 'Name',
        options: {
          required: true,
        },
      },
      {
        name: 'lastName',
        type: 'text-field',
        label: 'Last Name',
        options: {
          required: true,
        },
      },
      {
        name: 'dob',
        type: 'date-input',
        label: 'Birth Date',
        options: {
          error: false,
          required: false,
        },
      },
      {
        name: 'email',
        type: 'text-field',
        label: 'Email',
        options: {
          required: true,
          type: 'email',
        },
      },
      {
        name: 'phone',
        type: 'text-field',
        label: 'Phone',
        options: {
          required: true,
        },
      },
      {
        name: 'role',
        type: 'hidden',
        value: 'patient',
      },
    ],
    create: createPatientInvitation,
    read: patientCrud.read.bind(invitationCrud),
  },
  clinic: {
    fields: [
      {
        name: 'name',
        type: 'text-field',
        label: 'Facility Name',
        options: {
          required: true,
        },
      },
      {
        name: 'description',
        type: 'text-field',
        label: 'Description',
      },
      {
        name: 'address',
        type: 'text-field',
        label: 'Address',
      },
      {
        name: 'phone',
        type: 'text-field',
        label: 'Phone',
      },
      {
        name: 'clinicAdminName',
        type: 'text-field',
        label: 'Facility Admin Name',
        options: {
          required: true,
        },
      },
      {
        name: 'email',
        type: 'text-field',
        label: 'Facility Admin Email',
        options: {
          required: true,
          type: 'email',
        },
      },
    ],
    create: createClinic,
    update: clinicCrud.update.bind(clinicCrud),
    read: clinicCrud.read.bind(clinicCrud),
  },
};
